<i18n>
{
  "fr": {
    "configuratorCrossSellModal": {
      "quantityLoading": "Chargement des quantités...",
      "addToCart": "Ajouter",
      "addingToCart": "En cours...",
      "addedToCart": "Ajouté",
      "keepBrowsing": "Continuer mes achats",
      "goToCart": "Aller au panier"
    }
  }
}
</i18n>

<template>
  <MountingPortal
    :name="`${BASE_SELECTOR}--${id}`"
    append
    mount-to="#portal-container--footer"
  >
    <BaseModal
      :is-active="isModalActive"
      :close-on-backdrop-click="false"
      :class="`${BASE_SELECTOR}`"
      hide-close-button
      full-screen-until-xs
      @close="onClose"
    >
      <template #header></template>

      <template #default>
        <ConfiguratorCrossSellModalHeader />

        <ConfiguratorCrossSellRenderer
          v-if="activeRendererProduct"
          ref="renderer"
          :key="activeRendererProduct.size.id"
          :size="activeRendererProduct.size"
          :configuration="activeRendererProduct.configuration"
          :printing-area="activeRendererProduct.printingArea"
          :color="color"
          :customizations="customizations"
        />

        <ul :class="`${BASE_SELECTOR}__list`">
          <li
            v-for="compatibleProduct in compatibleProducts"
            :key="compatibleProduct.size.id"
            :class="`${BASE_SELECTOR}__list-item`"
          >
            <div :class="`${BASE_SELECTOR}__list-item-image-wrapper`">
              <img
                v-if="compatibleProduct.screenshot"
                :class="`${BASE_SELECTOR}__list-item-image`"
                :src="compatibleProduct.screenshot"
                :alt="compatibleProduct.size.name"
              />
              <LgfSpinner v-else :class="`${BASE_SELECTOR}__spinner`" />
            </div>
            <div :class="`${BASE_SELECTOR}__list-item-content`">
              <span :class="`${BASE_SELECTOR}__list-item-size-name`">{{
                compatibleProduct.size.name
              }}</span>

              <div :class="`${BASE_SELECTOR}__list-item-quantity-wrapper`">
                <ConfiguratorQuantitySelect
                  v-if="isQuantitiesInitialized"
                  v-model="selectedQuantities[compatibleProduct.size.id]"
                  :class="`${BASE_SELECTOR}__quantity-select`"
                  :name="`cross-sell-quantity-${compatibleProduct.size.id}`"
                  :use-v-model="true"
                  :disabled="
                    productHasBeenBought.includes(compatibleProduct.size.id) ||
                    productIsLoading.includes(compatibleProduct.size.id)
                  "
                  required
                />
                <div v-else>
                  {{ $t('configuratorCrossSellModal.quantityLoading') }}
                </div>

                <div :class="`${BASE_SELECTOR}__separator`"></div>

                <ConfiguratorFooterPriceSummary
                  :class="`${BASE_SELECTOR}__price-summary`"
                  :cup-price="
                    getPricingForProduct(compatibleProduct.size.id).cupPrice
                  "
                  :total-price="
                    getPricingForProduct(compatibleProduct.size.id).totalPrice
                  "
                  :total-price-without-vat="
                    getPricingForProduct(compatibleProduct.size.id)
                      .totalPriceWithoutVat
                  "
                  :should-display-total-price-without-vat="false"
                />
              </div>

              <BaseBtn
                :class="[
                  `${BASE_SELECTOR}__add-to-cart-button`,
                  {
                    [`${BASE_SELECTOR}__add-to-cart-button--adding-in-progress`]:
                      productIsLoading.includes(compatibleProduct.size.id),
                    [`${BASE_SELECTOR}__add-to-cart-button--already-bought`]:
                      productHasBeenBought.includes(compatibleProduct.size.id),
                  },
                ]"
                :disabled="
                  productHasBeenBought.includes(compatibleProduct.size.id) ||
                  productIsLoading.length > 0
                "
                :secondary="
                  !productHasBeenBought.includes(compatibleProduct.size.id)
                "
                @click="addToCart(compatibleProduct.size.id)"
              >
                {{
                  productIsLoading.includes(compatibleProduct.size.id)
                    ? $t('configuratorCrossSellModal.addingToCart')
                    : productHasBeenBought.includes(compatibleProduct.size.id)
                    ? $t('configuratorCrossSellModal.addedToCart')
                    : $t('configuratorCrossSellModal.addToCart')
                }}
              </BaseBtn>
            </div>
          </li>
        </ul>

        <div :class="`${BASE_SELECTOR}__action-wrapper`">
          <BaseBtn
            :class="`${BASE_SELECTOR}__keep-browsing-button`"
            :disabled="productIsLoading.length > 0"
            secondary
            block
            @click="routeToBlankConfigurator"
          >
            {{ $t('configuratorCrossSellModal.keepBrowsing') }}
          </BaseBtn>

          <BaseBtn
            v-if="cartUrl"
            :class="`${BASE_SELECTOR}__go-to-cart-button`"
            :disabled="productIsLoading.length > 0"
            :href="cartUrl"
            secondary
            block
          >
            {{ $t('configuratorCrossSellModal.goToCart') }}
          </BaseBtn>
        </div>
      </template>
    </BaseModal>
  </MountingPortal>
</template>

<script>
import BaseModal from '../BaseModal.vue';
import { MountingPortal } from 'portal-vue';
import { randomStr } from '../../utils/String';
import { ThemeType } from '../../api/static/ThemeType';
import { mapState as mapMiscState } from '../../store/modules/configurator/misc';
import { getCompatibleProductsFromStore } from '../../utils/CrossSellUtils';
import {
  uploadExports,
  submitToCart,
  buildCustomizationsData,
} from '../../utils/AddToCartUtils';
import {
  GlassesGetter,
  prefixType as prefixGlassesType,
} from '../../store/modules/configurator/glasses';
import { mapState as mapConfiguratorState } from '../../store/modules/configurator';
import ConfiguratorCrossSellRenderer from './ConfiguratorCrossSellRenderer';
import ConfiguratorQuantitySelect from './ConfiguratorQuantitySelect';
import ConfiguratorFooterPriceSummary from './ConfiguratorFooterPriceSummary';
import BaseBtn from '../BaseBtn.vue';
import ConfiguratorCrossSellModalHeader from './ConfiguratorCrossSellModalHeader';
import LgfSpinner from '../LgfSpinner.vue';

export default {
  name: 'ConfiguratorCrossSellModal',
  emits: ['close'],
  components: {
    BaseModal,
    MountingPortal,
    ConfiguratorCrossSellRenderer,
    ConfiguratorQuantitySelect,
    ConfiguratorFooterPriceSummary,
    BaseBtn,
    ConfiguratorCrossSellModalHeader,
    LgfSpinner,
  },
  props: {
    isModalActive: {
      type: Boolean,
      default: false,
    },
    productId: {
      type: Number,
      required: true,
    },
    sizeId: {
      type: Number,
      required: true,
    },
    quantityId: {
      type: Number,
      required: true,
    },
    themeType: {
      type: String,
      required: true,
    },
    color: {
      type: Object,
      required: true,
    },
    customizations: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      BASE_SELECTOR: 'configurator-cross-sell-modal',
      id: randomStr(),
      compatibleProducts: [],
      selectedQuantities: {}, // Store selected quantity for each product (key: sizeId, value: quantityId)
      productHasBeenBought: [],
      productIsLoading: [], // Track loading state for each product (array of sizeId)
      isQuantitiesInitialized: false,
      activeRendererProduct: null, // Single renderer product (mounted one at a time to limit WebGL contexts)
    };
  },
  computed: {
    ...mapMiscState(['cartUrl', 'blankConfiguratorUrl']),
    ...mapConfiguratorState(['csrfToken']),
  },
  watch: {
    isModalActive: {
      handler(isActive) {
        if (isActive) {
          this.calculateCompatibleProducts();
        }
      },
      immediate: true,
    },
    selectedQuantities: {
      handler() {
        // Force re-render of pricing by triggering reactivity
        this.$forceUpdate();
      },
      deep: true,
    },
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
    getPricingForProduct(sizeId) {
      // Find the product in compatibleProducts
      const product = this.compatibleProducts.find(p => p.size.id === sizeId);
      if (!product || !product.pricing) {
        return {
          cupPrice: null,
          totalPrice: null,
          totalPriceWithoutVat: null,
        };
      }

      // Get the selected quantity for this product
      const selectedQuantityId = this.selectedQuantities[sizeId];
      if (!selectedQuantityId) {
        return {
          cupPrice: product.pricing.cupPrice || null,
          totalPrice: null,
          totalPriceWithoutVat: null,
        };
      }

      // Find the variant for this product/size/quantity combination
      const findProductVariantGetter =
        this.$store.getters[
          prefixGlassesType(GlassesGetter.FIND_PRODUCT_VARIANT)
        ];

      const variant = findProductVariantGetter(
        this.themeType,
        sizeId,
        product.product.id,
        selectedQuantityId
      );

      if (!variant) {
        // If no variant found for selected quantity, return only unit price
        return {
          cupPrice: product.pricing.cupPrice,
          totalPrice: null,
          totalPriceWithoutVat: null,
        };
      }

      // Return pricing from the variant
      return {
        cupPrice: variant.unitPrice,
        totalPrice: variant.price,
        totalPriceWithoutVat: variant.priceWithoutVAT,
      };
    },
    async calculateCompatibleProducts() {
      // Reset state
      this.isQuantitiesInitialized = false;
      this.compatibleProducts = [];
      this.selectedQuantities = {};

      // Only proceed if theme type is GENERIC
      if (this.themeType !== ThemeType.GENERIC) {
        return;
      }

      // Calculate compatible products using shared util (resolves getters from store)
      const compatibleProducts = getCompatibleProductsFromStore(this.$store, {
        productId: this.productId,
        sizeId: this.sizeId,
        quantityId: this.quantityId,
        themeType: this.themeType,
      });

      // Generate screenshots and build structured data
      const structuredProducts = [];

      for (const compatibleProduct of compatibleProducts) {
        // Build structured entry first (without screenshot)
        const structuredEntry = {
          // Product information
          product: compatibleProduct.product,
          // Keep the full size object with all properties (gltfUrl, cameraPosition, etc.)
          size: compatibleProduct.size,
          configuration: compatibleProduct.configuration,
          printingArea: compatibleProduct.printingArea,
          productVariant: compatibleProduct.productVariant,

          // Price information
          pricing: {
            cupPrice: compatibleProduct.priceInfo?.cupPrice || null,
            cupPriceWithoutVat:
              compatibleProduct.priceInfo?.cupPriceWithoutVat || null,
            totalPrice: compatibleProduct.priceInfo?.totalPrice || null,
            totalPriceWithoutVat:
              compatibleProduct.priceInfo?.totalPriceWithoutVat || null,
            hasCompletePrice:
              compatibleProduct.priceInfo?.hasCompletePrice || false,
          },

          // Screenshot will be added later
          screenshot: null,
        };

        structuredProducts.push(structuredEntry);
      }

      // Replace with structured data (without screenshots yet)
      this.compatibleProducts = structuredProducts;

      // Generate screenshots one at a time to avoid multiple simultaneous WebGL contexts on iOS/Safari
      for (let i = 0; i < this.compatibleProducts.length; i++) {
        try {
          const product = this.compatibleProducts[i];

          // Mount renderer for this product only
          this.activeRendererProduct = product;
          await this.$nextTick();

          const renderer = this.$refs.renderer;
          if (renderer) {
            const screenshot = await renderer.generateScreenshot();
            this.$set(this.compatibleProducts[i], 'screenshot', screenshot);
          }
        } catch (error) {
          // Screenshot generation failed for this product; keep null
        } finally {
          // Unmount renderer to release WebGL context before next iteration
          this.activeRendererProduct = null;
          await this.$nextTick();
        }
      }

      // Initialize selected quantities with the current quantity from the original order
      // But check if that quantity exists for each product, otherwise use first available
      const findQuantityByIdGetter =
        this.$store.getters[
          prefixGlassesType(GlassesGetter.FIND_QUANTITY_BY_ID)
        ];
      const findProductVariantsGetter =
        this.$store.getters[
          prefixGlassesType(GlassesGetter.FIND_PRODUCT_VARIANTS)
        ];

      this.selectedQuantities = {};
      this.compatibleProducts.forEach(item => {
        // Get all variants for this product/size to extract available quantities
        const allVariants = findProductVariantsGetter(
          this.themeType,
          item.size.id,
          item.product.id
        );

        // Extract unique quantity IDs from variants
        const availableQuantityIds = [
          ...new Set(allVariants.map(v => v.quantityId)),
        ];

        // Get full quantity objects
        const availableQuantities = availableQuantityIds
          .map(qId => findQuantityByIdGetter(qId))
          .filter(q => q !== null && q !== undefined);

        // Check if original quantityId exists in available quantities
        const quantityExists = availableQuantities.some(
          q => q.id === this.quantityId
        );

        if (quantityExists) {
          this.$set(this.selectedQuantities, item.size.id, this.quantityId);
        } else if (availableQuantities.length > 0) {
          // Use first available quantity
          this.$set(
            this.selectedQuantities,
            item.size.id,
            availableQuantities[0].id
          );
        }
      });

      // Wait for next tick to ensure selectedQuantities are set before rendering selects
      await this.$nextTick();

      // Now we can show the quantity selects
      this.isQuantitiesInitialized = true;
    },
    async addToCart(sizeId) {
      // Find the product from local data
      const product = this.compatibleProducts.find(p => p.size.id === sizeId);
      if (!product) {
        return;
      }

      // Get selected quantity from local data
      const selectedQuantityId = this.selectedQuantities[sizeId];
      if (!selectedQuantityId) {
        return;
      }

      // Find the variant for this quantity from local data
      const findProductVariantGetter =
        this.$store.getters[
          prefixGlassesType(GlassesGetter.FIND_PRODUCT_VARIANT)
        ];

      const productVariant = findProductVariantGetter(
        this.themeType,
        sizeId,
        product.product.id,
        selectedQuantityId
      );

      if (!productVariant) {
        return;
      }

      // Verify CSRF token is available
      if (!this.csrfToken) {
        alert(
          'Erreur: Token CSRF non disponible. Veuillez rafraîchir la page.'
        );
        return;
      }

      // Mark as loading
      this.productIsLoading.push(sizeId);

      try {
        // Step 1: Export textures and 3D renders using renderer
        const exports = await this.generateExportsForProduct(sizeId);

        // Step 2: Upload all files using utility (including user images from IMAGE zones)
        const fileIdMap = await uploadExports(
          exports,
          this.csrfToken,
          this.customizations
        );

        // Step 3: Build customizations and submit to cart using utility

        // Get zones for this product's configuration
        const mapZonesGetter =
          this.$store.getters[prefixGlassesType(GlassesGetter.MAP_ZONES)];
        const targetZones = mapZonesGetter(product.configuration.zones);

        // Build customizations using utility (pass fileIdMap for IMAGE zones)
        const customizationsData = buildCustomizationsData(
          this.customizations,
          targetZones,
          fileIdMap
        );

        // Submit to cart using utility
        const response = await submitToCart(
          {
            productVariant,
            color: this.color,
            fileIdMap,
            customizations: customizationsData,
            isExpressDeliveryEnabled:
              this.$store.state.configurator.isExpressDeliveryEnabled,
          },
          this.csrfToken
        );

        globalThis.dispatchEvent(
          new CustomEvent('lgf.addToCartFromUpsell', {
            detail: {
              ecommerce: {
                items: [
                  {
                    ...response.data.addToCart.item,
                  },
                ],
              },
            },
          })
        );

        // Success
        this.productHasBeenBought.push(sizeId);
        document.dispatchEvent(
          new CustomEvent('checkoutChange', { detail: true })
        );
      } catch (error) {
        alert(
          `Une erreur est survenue lors de l'ajout de ${product.size.name} au panier.`
        );
      } finally {
        // Remove loading state
        const index = this.productIsLoading.indexOf(sizeId);
        if (index > -1) {
          this.productIsLoading.splice(index, 1);
        }
      }
    },

    /**
     * Generate all exports for a specific product using the renderer
     * @param {number} sizeId - Size ID to export
     * @returns {Promise<Object>} Object with texture72, texture300, renderCenter, renderLeft, renderRight, renderBack
     */
    async generateExportsForProduct(sizeId) {
      const product = this.compatibleProducts.find(p => p.size.id === sizeId);
      if (!product) {
        throw new Error(
          `[CrossSellModal] Product not found for sizeId: ${sizeId}`
        );
      }

      // Mount the renderer for this product
      this.activeRendererProduct = product;
      await this.$nextTick();

      const renderer = this.$refs.renderer;
      if (!renderer) {
        this.activeRendererProduct = null;
        throw new Error(
          `[CrossSellModal] Renderer not mounted for sizeId: ${sizeId}`
        );
      }

      try {
        return await renderer.generateAllExports();
      } finally {
        // Release WebGL context after export
        this.activeRendererProduct = null;
        await this.$nextTick();
        // Give Safari iOS time to reclaim the WebGL context and the large
        // print-resolution canvases before the next product is exported.
        // Without this breathing room their memory piles up across products
        // and the tab is killed.
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    },
    routeToBlankConfigurator() {
      window.location.href = this.blankConfiguratorUrl;
    },
  },
};
</script>

<style lang="scss" scoped>
.configurator-cross-sell-modal {
  &__header-title {
    text-align: center;
    font-weight: bold;
    font-size: 1.25rem;
  }

  &__list {
    list-style: none;
    padding: 1.25rem 0.75rem 0;
    display: flex;
    flex-direction: column;
    gap: 0.625rem;

    @include breakpoint($breakpoint-l) {
      padding: 1.25rem 3.6rem 0;
    }
  }

  &__list-item {
    margin: 0;
    display: flex;
    align-items: center;
    border-radius: 0.75rem;
    border: 1px solid $color-arapawa;
    padding: 0.625rem;
    height: 9.5rem;
  }

  &__list-item-image-wrapper {
    width: 45%;
    display: flex;
    align-items: center;
    justify-content: center;

    @include breakpoint($breakpoint-l) {
      flex-shrink: 0;
      width: 7rem;
    }
  }

  &__list-item-content {
    width: 100%;
  }

  &__list-item-size-name {
    font-size: 1.125rem;
    font-weight: 700;
    display: block;
  }

  &__spinner {
    width: 3rem;
    height: 3rem;
  }

  &__list-item-image {
    width: 6.5rem;
    height: 8rem;
    object-fit: contain;
  }

  &__list-item-quantity-wrapper {
    display: inline-flex;
    align-items: center;
    gap: 0.625rem;
  }

  &__separator {
    width: 1px;
    background-color: $color-arapawa;
    flex-shrink: 0;
    height: 2.625rem;
  }

  &__price-summary {
    color: $color-arapawa;
  }

  &__add-to-cart-button {
    background-color: $color-arapawa;
    display: block;
    font-size: 1rem;
    padding: 0.75rem 1.25rem;
    margin-top: 0.625rem;

    &:hover {
      opacity: 0.8;
    }

    @include breakpoint($breakpoint-l) {
      margin: 0 0.625rem 2rem;
      display: inline;
    }

    &::after {
      @extend %icon;
      @extend %icon--arrow-right1;

      margin-left: 0.625rem;
      display: inline-block;
    }

    &--adding-in-progress {
      background-color: transparent;
      color: $color-arapawa;
      border: 1px solid $color-arapawa;

      &::after {
        content: '';
        margin-left: 0;
      }
    }

    &--already-bought {
      background-color: transparent;
      color: $color-red-amaranth;
      border: 1px solid $color-red-amaranth;

      &::after {
        content: '';
        margin-left: 0;
      }

      &::before {
        @extend %icon;
        @extend %icon--check;

        margin-right: 0.625rem;
        display: inline-block;
      }
    }
  }

  &__action-wrapper {
    display: flex;
    flex-direction: column;
    padding: 0.625rem 0.75rem 0;
    gap: 0.625rem;

    @include breakpoint($breakpoint-l) {
      margin-top: 1rem;
      flex-direction: row;
      padding: 0 3.6rem;
      gap: 0.625rem;
    }
  }

  &__go-to-cart-button,
  &__keep-browsing-button {
    flex: 1;
    font-size: 1rem;
    padding: 0.75rem 1.25rem;
    min-height: 2.75rem;

    &:hover {
      opacity: 0.8;
    }

    @include breakpoint($breakpoint-l) {
      width: fit-content;
      margin: 0.625rem auto;
    }
  }

  &__keep-browsing-button {
    background-color: $color-gray-wild-sand;
    color: $color-arapawa;
    border: 1px solid $color-arapawa;
  }

  &__go-to-cart-button {
    background-color: $color-red-amaranth;
    color: $color-white;
    border: none;

    &::after {
      @extend %icon;
      @extend %icon--arrow-right1;

      margin-left: 0.5em;
    }
  }

  &::v-deep {
    .base-modal {
      &__body {
        background-color: $color-white;
      }

      &__header {
        @include breakpoint($breakpoint-l) {
          padding: 1.25rem 1.875rem;
        }
      }

      &__container {
        @include breakpoint($breakpoint-l) {
          max-width: 39.7rem;
        }
      }

      &__body-container {
        background-color: $color-white;
        padding: 0 0 1.25rem;
      }
    }

    .configurator-quantity-select {
      color: $color-arapawa;

      &:hover {
        opacity: 0.8;
      }

      &__label,
      &__select {
        font-size: 1rem;
        color: $color-arapawa;
      }
    }

    .configurator-footer-price-summary {
      &__price-value {
        font-size: 1rem;
      }
    }
  }
}
</style>
